import Keycloak from "keycloak-js";
import { Role } from "@/../generated/apollo";
import { MIN_SECONDS_BEFORE_REFRESH_KEYCLOAK_TOKEN } from "./tokenRefresh";

export const keycloak = new Keycloak("/backend/keycloak.json");

export const initializeKeycloak = async () => {
  // Access tokens live 300s, and nothing else renews them while the tab sits
  // idle: the Apollo link only refreshes on the way out of a request. A tab left
  // open long enough therefore reaches the backend with a token that expired
  // hours ago. Renew as soon as it lapses so an open tab keeps a live token.
  keycloak.onTokenExpired = () => {
    void keycloak
      .updateToken(MIN_SECONDS_BEFORE_REFRESH_KEYCLOAK_TOKEN)
      .catch(() => {
        // The refresh token is gone too (SSO session ended). Nothing to do here:
        // the next request surfaces the session notification to the user.
      });
  };

  return keycloak.init({
    onLoad: "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false, // Prevent Timeout when waiting for 3rd party check iframe message error
  });
};

declare global {
  interface Window {
    keycloak: Keycloak;
  }
}

// we sometimes need to access keycloak from browser in some scripts
if (typeof window !== "undefined") {
  window.keycloak = keycloak;
}

export const isClientViewer = () =>
  keycloak.hasRealmRole(Role.Viewer.toLowerCase());
export const isClientUser = () =>
  keycloak.hasRealmRole(Role.User.toLowerCase());
export const isClientAdmin = () =>
  keycloak.hasRealmRole(Role.Admin.toLowerCase());
export const isFxcAdmin = () =>
  keycloak.hasRealmRole(Role.FxcAdmin.toLowerCase());
export const isSuperuser = () =>
  keycloak.hasRealmRole(Role.Superuser.toLowerCase());

export const determineLoggedInUserRole = () => {
  if (isSuperuser()) {
    return Role.Superuser;
  }
  if (isClientAdmin()) {
    return Role.Admin;
  }
  if (isFxcAdmin()) {
    return Role.FxcAdmin;
  }
  if (isClientUser()) {
    return Role.User;
  }
  if (isClientViewer()) {
    return Role.Viewer;
  }
  return null;
};

export const hasRole = () =>
  isSuperuser() ||
  isClientUser() ||
  isClientAdmin() ||
  isFxcAdmin() ||
  isClientViewer();

/*
  This is a temporary solution to determine if the user is a demo client.
  We need to find a better way to determine if the user is a demo client.
  The current solution is to check if the user has a group that is not MasterCard or FXC.
  We use this to hide only MC specific features for other clients.
*/
export const isDemoClient = () =>
  !((keycloak.tokenParsed?.groups as string[] | undefined) ?? []).some(
    (group: string) => ["/MasterCard" /*, "/FXC"*/].includes(`${group}`),
  );
